import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { useConsent } from '../../context/ConsentContext';

/**
 * Explicit, per-category cookie preferences panel. Shared by ConsentBanner's
 * "Customize" action and CookiePolicy's "Manage Cookie Preferences" button —
 * both just call `openPreferences()` from ConsentContext, and this single
 * instance (mounted once, globally, alongside ConsentBanner) renders.
 *
 * Toggling a switch only edits local draft state; nothing is applied/persisted
 * until an explicit action (Save Preferences / Accept All / Reject
 * Non-Essential) is taken. Closing the dialog (Escape, backdrop click, the X
 * button) never applies a choice — it's a plain cancel, not a dismiss-as-accept.
 */
const ConsentPreferencesDialog = () => {
  const {
    consent, preferencesOpen, closePreferences, savePreferences, acceptAll, rejectNonEssential,
  } = useConsent();

  const [draft, setDraft] = useState({
    analytics: consent.analytics,
    visitorIdentification: consent.visitorIdentification,
  });

  // Reset the draft to the current persisted consent each time the panel opens.
  useEffect(() => {
    if (preferencesOpen) {
      setDraft({
        analytics: consent.analytics,
        visitorIdentification: consent.visitorIdentification,
      });
    }
  }, [preferencesOpen, consent.analytics, consent.visitorIdentification]);

  return (
    <Dialog
      open={preferencesOpen}
      onOpenChange={(open) => {
        if (!open) closePreferences();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
          <DialogDescription>
            Choose which optional cookies atEaseAI may use. You can change this choice at any
            time from the Cookie Policy page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium text-foreground">Essential</p>
              <p className="text-xs text-muted-foreground">
                Required for sign-in, session and security. Cannot be disabled.
              </p>
            </div>
            <Switch checked disabled aria-label="Essential cookies, always on" />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
            <div>
              <Label htmlFor="consent-analytics" className="text-sm font-medium text-foreground">
                Analytics
              </Label>
              <p className="text-xs text-muted-foreground">
                Google Analytics (GA4) and PostHog help us understand product usage and improve
                atEaseAI.
              </p>
            </div>
            <Switch
              id="consent-analytics"
              checked={draft.analytics}
              onCheckedChange={(checked) => setDraft((d) => ({ ...d, analytics: checked }))}
              aria-label="Analytics cookies"
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
            <div>
              <Label htmlFor="consent-visitor-id" className="text-sm font-medium text-foreground">
                Visitor Identification
              </Label>
              <p className="text-xs text-muted-foreground">
                RB2B identifies companies visiting our site so our sales team can follow up.
              </p>
            </div>
            <Switch
              id="consent-visitor-id"
              checked={draft.visitorIdentification}
              onCheckedChange={(checked) => setDraft((d) => ({ ...d, visitorIdentification: checked }))}
              aria-label="Visitor identification cookies"
            />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button variant="default" onClick={rejectNonEssential} className="w-full sm:w-auto">
            Reject Non-Essential
          </Button>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button variant="default" onClick={acceptAll} className="w-full sm:w-auto">
              Accept All
            </Button>
            <Button variant="outline" onClick={() => savePreferences(draft)} className="w-full sm:w-auto">
              Save Preferences
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentPreferencesDialog;
